// deepgramService.ts — Deepgram streaming STT client
//
// Opens a WebSocket to Deepgram's real-time transcription API.
// Sends raw audio chunks (linear16, 16 kHz mono), receives interim and
// final transcripts. Manages connection lifecycle.
//
// Usage:
//   const conn = await openDeepgramStream('da', onTranscript, onError);
//   conn.send(audioBuffer);
//   conn.close();

import { getAuth } from 'firebase/auth';

export interface TranscriptEvent {
  type: 'interim' | 'final';
  text: string;
  confidence: number;
}

export interface DeepgramConnection {
  send: (audioData: ArrayBuffer) => void;
  close: () => void;
  isOpen: () => boolean;
}

/** Fetch a short-lived Deepgram key from our API endpoint. */
async function fetchTemporaryKey(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const res = await fetch('/api/voice-token', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }

  const { key } = await res.json() as { key: string };
  return key;
}

/**
 * Open a streaming STT WebSocket to Deepgram.
 *
 * @param language   BCP-47 language code (e.g. 'da' for Danish, 'en' for English)
 * @param onTranscript  Callback for interim and final transcript events
 * @param onError    Callback for connection or protocol errors
 * @returns A DeepgramConnection with send/close/isOpen methods
 */
export async function openDeepgramStream(
  language: string,
  onTranscript: (event: TranscriptEvent) => void,
  onError: (error: string) => void,
): Promise<DeepgramConnection> {
  const apiKey = await fetchTemporaryKey();

  const params = new URLSearchParams({
    model: 'nova-3',
    language,
    punctuate: 'true',
    interim_results: 'true',
    endpointing: '300',
    encoding: 'linear16',
    sample_rate: '16000',
    channels: '1',
  });

  // Deepgram WebSocket STT endpoint — auth via subprotocol token
  const ws = new WebSocket(
    `wss://api.deepgram.com/v1/listen?${params}`,
    ['token', apiKey],
  );

  ws.binaryType = 'arraybuffer';

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data as string) as {
        is_final?: boolean;
        channel?: { alternatives?: Array<{ transcript?: string; confidence?: number }> };
      };
      const transcript = data.channel?.alternatives?.[0]?.transcript;
      if (!transcript) return;

      onTranscript({
        type: data.is_final ? 'final' : 'interim',
        text: transcript,
        confidence: data.channel?.alternatives?.[0]?.confidence ?? 0,
      });
    } catch {
      // Skip malformed messages — Deepgram occasionally sends non-JSON metadata
    }
  };

  ws.onclose = (event) => {
    // 1000 = normal closure (user stopped recording); anything else is unexpected
    if (event.code !== 1000) {
      onError(`Connection closed: ${event.reason || String(event.code)}`);
    }
  };

  // Wait for connection to open
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Connection timeout')), 5000);
    ws.onopen = () => { clearTimeout(timeout); resolve(); };
    ws.onerror = () => { clearTimeout(timeout); reject(new Error('WebSocket connection error')); };
  });

  // Restore runtime error handler after successful open
  ws.onerror = () => onError('WebSocket connection error');

  return {
    send: (audioData: ArrayBuffer) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(audioData);
      }
    },
    close: () => {
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, 'User stopped recording');
      }
    },
    isOpen: () => ws.readyState === WebSocket.OPEN,
  };
}
